import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Send, ArrowLeft, MessageSquare, FileText, Plus, X, Coins, CalendarClock } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

export default function Messages() {
  const { id } = useParams();
  if (id) return <Thread id={id} />;
  return <ConversationList />;
}

function ConversationList() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/marketplace/conversations").then((r) => setList(r.data.conversations)).catch(() => setList([])); }, []);
  if (list === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="messages-list">
      <h1 className="font-display text-3xl font-extrabold">Messagerie</h1>
      {list.length === 0 ? <div className="text-center py-14 text-zinc-500 text-sm" data-testid="messages-empty">Aucune conversation. Elles s'ouvrent quand une proposition est acceptée.</div> : (
        <div className="space-y-2">
          {list.map((c) => (
            <button key={c.id} onClick={() => navigate(`/messages/${c.id}`)} data-testid={`conv-${c.id}`}
              className="w-full text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 hover:border-amber-500/50">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold truncate">{c.other_name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-500">{c.role === "owner" ? "Demandeur" : "Pro"}</span>
              </div>
              <p className="text-xs text-zinc-500 truncate mt-1">{c.request_title}</p>
              {c.last_message && <p className="text-sm text-zinc-400 truncate mt-1">{c.last_message}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Thread({ id }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = useCallback(() => api.get(`/marketplace/conversations/${id}`).then((r) => setData(r.data)).catch(() => setData(false)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/marketplace/conversations/${id}/messages`, { text }); setText(""); load(); }
    catch { toast.error("Échec de l'envoi"); }
  };

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  if (data === false) return <div className="text-center py-20 text-zinc-500" data-testid="conv-not-found">Conversation introuvable.</div>;

  return (
    <div className="max-w-2xl flex flex-col h-[calc(100vh-8rem)]" data-testid="conversation-thread">
      <button onClick={() => navigate("/messages")} className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Conversations</button>
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-5 h-5 text-amber-400" />
        <div><p className="font-semibold">{data.other_name}</p><p className="text-xs text-zinc-500">{data.request_title}</p></div>
      </div>

      <QuotePanel conversationId={id} role={data.role} onChange={load} />

      <div className="flex-1 overflow-y-auto space-y-2 bg-[#0F0F13] border border-zinc-800 rounded-2xl p-4" data-testid="messages-scroll">
        {(data.messages || []).length === 0 ? <p className="text-sm text-zinc-500 text-center py-8">Démarrez la conversation.</p> :
          data.messages.map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "ml-auto gold-gradient text-black" : "bg-zinc-800 text-zinc-200"}`} data-testid={`msg-${m.id}`}>
              {m.text}
            </div>
          ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="msg-input"
          placeholder="Écris ton message…" className="flex-1 h-11 px-4 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500/50" />
        <button onClick={send} data-testid="msg-send" className="h-11 px-4 rounded-xl gold-gradient text-black font-semibold inline-flex items-center gap-1"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const QSTATUS = {
  SENT: { label: "En attente", cls: "text-amber-400" },
  ACCEPTED: { label: "Accepté · paiement en cours", cls: "text-sky-400" },
  PAID: { label: "Payé", cls: "text-emerald-400" },
  DECLINED: { label: "Refusé", cls: "text-zinc-500" },
};

function QuotePanel({ conversationId, role, onChange }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", amount: "", message: "", delay: "" });
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const isPro = role === "pro";

  const load = useCallback(() => api.get(`/marketplace/quotes?conversation_id=${conversationId}`).then((r) => setQuotes(r.data.quotes)).catch(() => setQuotes([])), [conversationId]);
  useEffect(() => { load(); }, [load]);

  const createQuote = async () => {
    if (!form.title.trim() || !form.amount) { toast.error("Titre et montant requis"); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/quotes", { conversation_id: conversationId, title: form.title, amount: Number(form.amount), message: form.message, delay: form.delay });
      toast.success("Devis envoyé"); setForm({ title: "", amount: "", message: "", delay: "" }); setCreating(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };

  const startAccept = async (q) => {
    try {
      const { data } = await api.get(`/marketplace/availability?pro_id=${q.pro_id}`);
      if (data.slots && data.slots.length > 0) { setPicker({ quote: q, slots: data.slots }); setSelectedSlot(""); return; }
    } catch { /* pas de créneaux → paiement direct */ }
    doAccept(q, null);
  };
  const doAccept = async (q, slotId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/quotes/${q.id}/accept`, { origin_url: window.location.origin, slot_id: slotId || undefined });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Échec du paiement"); setBusy(false); }
  };
  const decline = async (q) => {
    try { await api.post(`/marketplace/quotes/${q.id}/decline`); toast.success("Devis refusé"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="mb-3 bg-[#141419] border border-zinc-800 rounded-2xl p-4" data-testid="quote-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-amber-400" />Devis</span>
        {isPro && !creating && <button onClick={() => setCreating(true)} data-testid="quote-create-btn" className="text-xs text-amber-400 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Créer un devis</button>}
      </div>

      {isPro && creating && (
        <div className="space-y-2 mb-3 border border-zinc-800 rounded-xl p-3" data-testid="quote-form">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400">Nouveau devis</span>
            <button onClick={() => setCreating(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
          </div>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="quote-title" placeholder="Intitulé de la prestation" className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="quote-amount" type="number" placeholder="Montant €" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
            <input value={form.delay} onChange={(e) => setForm({ ...form, delay: e.target.value })} data-testid="quote-delay" placeholder="Délai (ex : 10 jours)" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
          </div>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quote-message" rows={2} placeholder="Détails / conditions" className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100 resize-none" />
          <button onClick={createQuote} disabled={busy} data-testid="quote-submit" className="h-9 px-4 rounded-lg gold-gradient text-black text-sm font-semibold disabled:opacity-60">Envoyer le devis</button>
        </div>
      )}

      {quotes.length === 0 ? <p className="text-xs text-zinc-500">{isPro ? "Aucun devis. Crée-en un pour proposer un prix et être payé en toute sécurité." : "Aucun devis reçu pour le moment."}</p> : (
        <div className="space-y-2">
          {quotes.map((q) => {
            const st = QSTATUS[q.status] || QSTATUS.SENT;
            return (
              <div key={q.id} className="border border-zinc-800 rounded-xl p-3" data-testid={`quote-${q.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{q.title}</p>
                    {q.message && <p className="text-xs text-zinc-500 mt-0.5 whitespace-pre-line">{q.message}</p>}
                    {q.delay && <p className="text-[11px] text-zinc-500 mt-0.5">Délai : {q.delay}</p>}
                  </div>
                  <span className="text-sm font-bold text-amber-400 inline-flex items-center gap-1 shrink-0"><Coins className="w-3.5 h-3.5" />{q.amount?.toFixed(2)} €</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span className={`text-[11px] font-medium ${st.cls}`} data-testid={`quote-status-${q.id}`}>{st.label}</span>
                  <div className="flex items-center gap-2">
                    {q.order_id && (q.status === "ACCEPTED" || q.status === "PAID") && (
                      <button onClick={() => navigate(`/commandes?paid=${q.order_id}`)} data-testid={`quote-order-${q.id}`} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-300">Voir la commande</button>
                    )}
                    {q.is_client && q.status === "SENT" && (
                      <>
                        <button onClick={() => startAccept(q)} disabled={busy} data-testid={`quote-accept-${q.id}`} className="text-[11px] px-2.5 h-7 rounded-lg gold-gradient text-black font-semibold disabled:opacity-60">Accepter & payer</button>
                        <button onClick={() => decline(q)} data-testid={`quote-decline-${q.id}`} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-400">Refuser</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {picker && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="slot-picker-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPicker(null)} />
          <div className="relative bg-[#141419] border border-zinc-800 rounded-2xl p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-amber-400" /><h3 className="font-display font-bold">Choisis un créneau</h3></div>
            <p className="text-xs text-zinc-500 mb-3">Sélectionne une date de prestation avant de payer.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {picker.slots.map((s) => (
                <button key={s.id} onClick={() => setSelectedSlot(s.id)} data-testid={`pick-slot-${s.id}`}
                  className={`w-full text-left px-3 py-2 rounded-xl border text-sm ${selectedSlot === s.id ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-zinc-800 text-zinc-300"}`}>
                  {s.start.slice(0, 16).replace("T", " ")} → {s.end.slice(11, 16)}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setPicker(null)} className="text-xs px-3 h-9 rounded-lg text-zinc-400">Annuler</button>
              <button onClick={() => doAccept(picker.quote, selectedSlot)} disabled={!selectedSlot || busy} data-testid="slot-confirm-pay"
                className="text-xs px-4 h-9 rounded-lg gold-gradient text-black font-semibold disabled:opacity-50">Réserver & payer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
